// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; captions label the adjacent control (same convention as the other admin screens).
//
// =============================================================================
// TopUpSORulesAdmin — Manage → Sales Order Sync → Top-up Requests → SO Rules
// =============================================================================
// Admin/SuperUser screen over `top_up_so_rules` (db-159): per Stock-Top-up section
// (and the Other-Stock $150 variant), how it becomes Sales Orders. The Top-up SO
// builder reads this at Confirm & Send. Editable so behaviour changes without a
// deploy. It also owns the SO order-number SUFFIX (db-160 moved it here from the
// Product Mapping); product codes live on the Product Mapping screen.
// Reference: docs/stock-top-ups-plan.md §G.
// =============================================================================
import React from "react";
import { listTopUpSoRules, updateTopUpSoRuleById } from "../../data/topUpSoRules";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";

const LINE_MODES = [
  { value: "per_unit", label: "One SO per unit (e.g. each Stationery Pack)" },
  { value: "summary", label: "One SO, single summary line" },
  { value: "amount", label: "One SO, amount line (e.g. $150 reimbursement)" },
];

function rowLabel(section, variant) {
  switch (section) {
    case "packs":
      return "Stationery Packs";
    case "bulk":
      return "Bulk Stationery";
    case "bags":
      return "School Bags";
    case "hats":
      return "Hats";
    case "socks":
      return "Socks";
    case "other":
      return variant === "reimbursement"
        ? "Other Stock — $150 Reimbursement"
        : "Other Stock — Lunch Boxes / Water Bottles";
    default:
      return `${section}${variant ? ` — ${variant}` : ""}`;
  }
}

export function TopUpSORulesAdmin() {
  const [rows, setRows] = React.useState([]);
  const [edits, setEdits] = React.useState({}); // id -> { line_mode, include_discount, deactivated }
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState("");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [tick, setTick] = React.useState(0);

  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      listTopUpSoRules(true).then(function (data) {
        if (cancelled) return;
        setRows(data || []);
        const e = {};
        for (const r of data || []) {
          e[r.id] = {
            line_mode: r.line_mode,
            include_discount: !!r.include_discount,
            suffix: r.suffix || "",
            deactivated: !!r.deactivated,
          };
        }
        setEdits(e);
        setLoading(false);
      });
      return function () {
        cancelled = true;
      };
    },
    [tick]
  );

  function setField(id, key, value) {
    setEdits(function (prev) {
      return { ...prev, [id]: { ...prev[id], [key]: value } };
    });
  }

  function showSaved(msg) {
    setSavedMsg(msg);
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  async function saveRow(r) {
    const patch = {
      line_mode: edits[r.id]?.line_mode,
      include_discount: !!edits[r.id]?.include_discount,
      suffix: (edits[r.id]?.suffix || "").trim(),
      deactivated: !!edits[r.id]?.deactivated,
    };
    if (!patch.suffix) {
      showSaved("A suffix is required.");
      return;
    }
    setSavingId(r.id);
    const { error } = await updateTopUpSoRuleById(r.id, patch);
    setSavingId("");
    if (error) {
      showSaved(`Save failed: ${error.message}`);
      return;
    }
    logAudit("top_up_so_rule_set", { section: r.section, variant: r.variant, ...patch });
    setTick(function (n) {
      return n + 1;
    });
    showSaved("Rule saved ✓");
  }

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="mb-1 text-[12px] text-zephyr-muted font-semibold">
        Sales Order Sync · Top-up Requests
      </div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="m-0 text-[22px] text-zephyr-text">Top-up SO Rules</h1>
        {savedMsg && (
          <span
            className={`text-[13px] font-semibold ${
              savedMsg.includes("failed") ? "text-[#C62828]" : "text-[#2E7D32]"
            }`}
          >
            {savedMsg}
          </span>
        )}
      </div>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        For each section, choose how it becomes Sales Orders at Confirm &amp; Send.{" "}
        <strong>One SO per unit</strong> makes a separate SO for each approved pack (Stationery
        Packs). <strong>Single summary line</strong> puts the whole section on one SO line with the
        breakdown in the comment (the bulk sections). <strong>Amount line</strong> carries the fixed
        amount (the $150 reimbursement). The <strong>discount line</strong> adds a $0 DISCOUNT line
        to each SO. The <strong>Suffix</strong> is the SO order-number tag; the Stationery Packs'{" "}
        <code>STA</code> is the <strong>base</strong> the counter increments (<code>STA</code>,{" "}
        <code>STA1</code>, <code>STA2</code>…). Product codes are set on the{" "}
        <strong>Product Mapping</strong> screen.
      </p>

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading…</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid items-center gap-3 py-[8px] px-[13px] bg-zephyr-panel-bg text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px]"
            style={{ gridTemplateColumns: "1fr 300px 90px 100px 80px 80px" }}
          >
            <div>Section</div>
            <div>How it becomes SOs</div>
            <div>Suffix</div>
            <div>Discount</div>
            <div>Active</div>
            <div />
          </div>
          {rows.map(function (r, idx) {
            const e = edits[r.id] || {};
            const dirty =
              e.line_mode !== r.line_mode ||
              e.include_discount !== !!r.include_discount ||
              e.suffix !== (r.suffix || "") ||
              e.deactivated !== !!r.deactivated;
            return (
              <div
                key={r.id}
                className="grid items-center gap-3 py-[9px] px-[13px]"
                style={{
                  gridTemplateColumns: "1fr 300px 90px 100px 80px 80px",
                  borderTop: "1px solid #EEF3F2",
                  background: idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                  opacity: e.deactivated ? 0.6 : 1,
                }}
              >
                <label className="text-[13px] text-zephyr-text font-semibold">
                  {rowLabel(r.section, r.variant)}
                </label>
                <select
                  aria-label={`Line mode for ${rowLabel(r.section, r.variant)}`}
                  value={e.line_mode}
                  onChange={function (ev) {
                    setField(r.id, "line_mode", ev.target.value);
                  }}
                  className="py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-full"
                  style={{ fontFamily: "inherit" }}
                >
                  {LINE_MODES.map(function (m) {
                    return (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  aria-label={`SO suffix for ${rowLabel(r.section, r.variant)}`}
                  value={e.suffix ?? ""}
                  onChange={function (ev) {
                    setField(r.id, "suffix", ev.target.value);
                  }}
                  className="py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-full"
                  style={{ fontFamily: "inherit" }}
                />
                <label className="flex items-center gap-2 text-[13px] text-zephyr-text">
                  <input
                    type="checkbox"
                    checked={!!e.include_discount}
                    onChange={function (ev) {
                      setField(r.id, "include_discount", ev.target.checked);
                    }}
                    className="w-[16px] h-[16px] cursor-pointer"
                  />
                  <span>Add</span>
                </label>
                <label className="flex items-center gap-2 text-[13px] text-zephyr-text">
                  <input
                    type="checkbox"
                    checked={!e.deactivated}
                    onChange={function (ev) {
                      setField(r.id, "deactivated", !ev.target.checked);
                    }}
                    className="w-[16px] h-[16px] cursor-pointer"
                  />
                  <span>On</span>
                </label>
                <Btn
                  onClick={function () {
                    saveRow(r);
                  }}
                  disabled={!dirty || savingId === r.id}
                >
                  {savingId === r.id ? "…" : "Save"}
                </Btn>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
